import React from 'react';
import {Avatar, Chip, Divider, Menu, MenuItem, Stack, Tooltip, Typography} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UserAvatar from '../../../components/UserAvatar';
import {generateUserColor} from '../../../components/prosemirror-editor/helpers/utils/users';
import {AI_AUTHOR, AI_AUTHOR_UID} from '../ai/chartAuthorship';
import {
    AUTHOR_DIVIDER_WIDTH,
    AUTHOR_LABEL_WIDTH,
    countFittingAuthorChips,
} from '../utils/countFittingAuthorChips';

const CHIP_SX = {
    height: 22,
    maxWidth: 160,
    fontSize: 11,
    '& .MuiChip-label': {
        paddingLeft: '2px',
        paddingRight: '10px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '& .MuiChip-avatar': {
        width: 16,
        height: 16,
        marginLeft: '6px',
        marginRight: '8px',
    },
};

function authorLabel(author) {
    const isAI = author.uid === AI_AUTHOR_UID || author.isAI;
    return author.displayName || (isAI ? AI_AUTHOR.displayName : 'Unknown');
}

function AuthorChip({author, selected, onClick}) {
    const isAI = author.uid === AI_AUTHOR_UID || author.isAI;
    const userColor = generateUserColor(author.uid);
    const label = authorLabel(author);

    return (
        <Chip
            data-testid={`chart-author-chip-${author.uid}`}
            avatar={
                isAI ? (
                    <Avatar sx={{bgcolor: 'white'}}>
                        <SmartToyIcon sx={{fontSize: 12}}/>
                    </Avatar>
                ) : author.uid ? (
                    <UserAvatar user={author} userId={author.uid} sx={{width: 16, height: 16}}/>
                ) : (
                    <Avatar sx={{bgcolor: userColor}}>{label.charAt(0)}</Avatar>
                )
            }
            label={label}
            onClick={onClick ? () => onClick(author.uid) : undefined}
            clickable={Boolean(onClick)}
            variant={selected ? 'filled' : 'outlined'}
            sx={{
                ...CHIP_SX,
                borderColor: selected ? userColor : undefined,
                bgcolor: selected ? `${userColor}22` : undefined,
                borderWidth: 1,
                '&:hover': {bgcolor: `${userColor}33`},
            }}
        />
    );
}

const MAX_INLINE_CHIPS = 3;

/**
 * Compact AUTHORS strip for the whiteboard toolbar (left of REACH).
 * Fits as many chips as the current slot allows; leftover authors go behind +N.
 */
export default function ChartAuthorsBar({
    authors = [],
    selectedAuthorIds = [],
    onAuthorClick,
}) {
    const [overflowEl, setOverflowEl] = React.useState(null);
    const rootRef = React.useRef(null);
    const labelRef = React.useRef(null);
    const [availableWidth, setAvailableWidth] = React.useState(null);
    const [labelWidth, setLabelWidth] = React.useState(AUTHOR_LABEL_WIDTH);

    React.useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const apply = (width) => {
            const next = Math.round(width);
            if (next <= 0) return;
            setAvailableWidth((prev) => {
                if (prev != null && Math.abs(prev - next) < 4) return prev;
                return next;
            });
        };

        const observer = new ResizeObserver(([entry]) => {
            apply(entry.contentRect.width);
        });
        apply(el.getBoundingClientRect().width);
        observer.observe(el);
        return () => observer.disconnect();
    }, [authors.length]);

    React.useLayoutEffect(() => {
        const measured = labelRef.current?.getBoundingClientRect().width;
        if (measured > 0) {
            const next = Math.ceil(measured) + 6;
            setLabelWidth((prev) => (prev === next ? prev : next));
        }
    }, [authors.length]);

    if (!authors.length) {
        return null;
    }

    const labels = authors.map(authorLabel);
    const fallbackInline = Math.min(authors.length, MAX_INLINE_CHIPS);
    const inlineCount = availableWidth > 0
        ? countFittingAuthorChips({
            availableWidth,
            labels,
            labelWidth,
            dividerWidth: AUTHOR_DIVIDER_WIDTH,
        })
        : fallbackInline;
    const inline = authors.slice(0, inlineCount);
    const overflow = authors.slice(inlineCount);

    return (
        <Stack
            ref={rootRef}
            data-testid="chart-authors-bar"
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0}
            sx={{minWidth: 0, flex: '1 1 0', height: '100%', overflow: 'hidden'}}
        >
            <Tooltip
                title="Highlight cards and edges last edited by an author"
                placement="bottom"
                enterDelay={300}
                arrow
            >
                <Typography
                    ref={labelRef}
                    variant="caption"
                    color="text.disabled"
                    sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        flexShrink: 0,
                        cursor: 'default',
                        mr: 0.75,
                    }}
                >
                    Authors
                </Typography>
            </Tooltip>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{minWidth: 0, overflow: 'hidden'}}>
                {inline.map((author) => (
                    <AuthorChip
                        key={author.uid}
                        author={author}
                        selected={selectedAuthorIds.includes(author.uid)}
                        onClick={onAuthorClick}
                    />
                ))}
                {overflow.length > 0 ? (
                    <>
                        <Chip
                            data-testid="chart-authors-overflow"
                            label={`+${overflow.length}`}
                            size="small"
                            variant="outlined"
                            onClick={(event) => setOverflowEl(event.currentTarget)}
                            sx={{...CHIP_SX, minWidth: 36, maxWidth: 48, flexShrink: 0}}
                        />
                        <Menu
                            anchorEl={overflowEl}
                            open={Boolean(overflowEl)}
                            onClose={() => setOverflowEl(null)}
                        >
                            {overflow.map((author) => (
                                <MenuItem
                                    key={author.uid}
                                    data-testid={`chart-authors-overflow-item-${author.uid}`}
                                    onClick={() => onAuthorClick(author.uid)}
                                    selected={selectedAuthorIds.includes(author.uid)}
                                    sx={{py: 0.5}}
                                >
                                    <AuthorChip
                                        author={author}
                                        selected={selectedAuthorIds.includes(author.uid)}
                                    />
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                ) : null}
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ml: 1}}/>
        </Stack>
    );
}
